/**
 * usePagination Hook
 * Reusable pagination logic for lists
 *
 * Usage:
 * const { paginatedItems, pagination } = usePagination(items, { perPage: 10 });
 * <Pagination {...pagination} />
 */

import { useState, useMemo, useCallback } from 'react';
import { DEFAULT_ITEMS_PER_PAGE } from '@/config/constants';

interface UsePaginationOptions {
    perPage?: number;
    initialPage?: number;
}

interface PaginationState {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    perPage: number;
    startIndex: number;
    endIndex: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
    goToPage: (page: number) => void;
    nextPage: () => void;
    prevPage: () => void;
    reset: () => void;
}

export function usePagination<T>(
    items: T[],
    options: UsePaginationOptions = {}
): {
    paginatedItems: T[];
    pagination: PaginationState;
} {
    const { perPage = DEFAULT_ITEMS_PER_PAGE, initialPage = 1 } = options;
    const [currentPage, setCurrentPage] = useState(initialPage);

    const totalItems = items.length;
    const totalPages = Math.ceil(totalItems / perPage);

    // Reset to page 1 if current page exceeds total pages
    const safePage = Math.min(Math.max(1, currentPage), Math.max(1, totalPages));

    const startIndex = (safePage - 1) * perPage;
    const endIndex = Math.min(startIndex + perPage, totalItems);

    const paginatedItems = useMemo(() => {
        return items.slice(startIndex, endIndex);
    }, [items, startIndex, endIndex]);

    const goToPage = useCallback((page: number) => {
        const newPage = Math.min(Math.max(1, page), totalPages);
        setCurrentPage(newPage);
    }, [totalPages]);

    const nextPage = useCallback(() => {
        if (safePage < totalPages) {
            setCurrentPage(safePage + 1);
        }
    }, [safePage, totalPages]);

    const prevPage = useCallback(() => {
        if (safePage > 1) {
            setCurrentPage(safePage - 1);
        }
    }, [safePage]);

    const reset = useCallback(() => {
        setCurrentPage(initialPage);
    }, [initialPage]);

    const pagination: PaginationState = {
        currentPage: safePage,
        totalPages,
        totalItems,
        perPage,
        startIndex: startIndex + 1, // 1-indexed for display
        endIndex,
        hasNextPage: safePage < totalPages,
        hasPrevPage: safePage > 1,
        goToPage,
        nextPage,
        prevPage,
        reset,
    };

    return { paginatedItems, pagination };
}

export default usePagination;
